// @djsem/analysis-contract — Transcription Pipeline v0.8B (Prompt 13).
//
// Canonical transcript contract + a pure, provider-agnostic pipeline service. The live
// transcription RUNTIME (Cloudflare Workers AI Whisper binding, R2 audio, D1 jobs) lives in
// apps/transcription-worker and is NOT duplicated here. Manual import always works even
// with no provider. Instrumental tracks / no-lyrics are valid. A failing provider yields a
// FAILED job, never a crash. Transcripts are metadata: rights + usage policy are explicit;
// restricted rights block public export.

import { deterministicHash } from "./prepWorkspace";
import { SeqCounter } from "@djsem/shared";
import { type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult } from "@djsem/shared";

// ---- contracts --------------------------------------------------------------

/** Where a transcript (or a transcription provider) originates from. */
export type DJSEMTranscriptSourceType =
  | "manual_import" | "cloudflare_workers_ai_whisper" | "external_provider" | "imported" | "none" | "unknown";

/** A single timed segment of a transcript. */
export interface DJSEMTranscriptSegment {
  id: string;
  startTimeMs?: number;
  endTimeMs?: number;
  text: string;
  confidence: number;
  speaker?: string;
  warnings: string[];
}

/** A transcript's segments aligned to phrases and (optionally) suggested chapter boundaries. */
export interface DJSEMTranscriptPhraseAlignment {
  alignmentVersion: string;
  phrases: Array<{ id: string; startTimeMs?: number; endTimeMs?: number; text?: string; confidence: number; suggestedChapterId?: string; warnings: string[] }>;
}

/** The canonical v0.8B transcript artifact for a track: segments, rights status, and usage policy. */
export interface DJSEMTranscriptArtifact {
  transcriptId: string;
  trackId: string;
  transcriptVersion: string;
  sourceType: DJSEMTranscriptSourceType;
  language?: string;
  segments: DJSEMTranscriptSegment[];
  fullText?: string;
  confidence: number;
  rightsStatus: "unknown" | "owned" | "licensed" | "restricted";
  usagePolicy: {
    canDisplayInPrep: boolean;
    canDisplayInPublicExport: boolean;
    canUseForSemanticAnalysis: boolean;
    warnings: string[];
  };
  phraseAlignment?: DJSEMTranscriptPhraseAlignment;
  audit: { createdAt: string; provider: DJSEMTranscriptSourceType; warnings: string[]; fallbackUsed: boolean };
}

/** Lifecycle status of a transcription job. */
export type DJSEMTranscriptionJobStatus = "queued" | "running" | "completed" | "failed" | "skipped_instrumental";

/** The result of running (or queuing) a transcription job. */
export interface TranscriptionJobResult {
  jobId: string;
  trackId: string;
  status: DJSEMTranscriptionJobStatus;
  provider: DJSEMTranscriptSourceType;
  artifact?: DJSEMTranscriptArtifact;
  warnings: string[];
  error?: string;
}

// ---- provider abstraction ---------------------------------------------------

/** Input for a {@link DJSEMTranscriptionProvider}'s canTranscribe() call. */
export interface TranscriptionProviderCanRunInput { trackId: string; hasAudio: boolean; language?: string; }
/** Input for a {@link DJSEMTranscriptionProvider}'s transcribe() call. */
export interface TranscriptionProviderInput { trackId: string; language?: string; audioAvailable: boolean; createdAt?: string; }

/** Interface every transcription provider (real runtime binding or manual/stub) must implement. */
export interface DJSEMTranscriptionProvider {
  providerId: DJSEMTranscriptSourceType;
  canTranscribe(input: TranscriptionProviderCanRunInput): Promise<boolean>;
  transcribe(input: TranscriptionProviderInput): Promise<DJSEMTranscriptArtifact>;
}

// ---- inputs -----------------------------------------------------------------

/** Input for {@link TranscriptionPipelineService.requestTranscription}. */
export interface RequestTranscriptionInput {
  trackId: string; provider?: DJSEMTranscriptSourceType; language?: string; hasAudio?: boolean;
  isInstrumental?: boolean; idempotencyKey?: string; existingJobKeys?: string[]; createdAt?: string;
}
/** Input for {@link TranscriptionPipelineService.runQueuedJob}. */
export interface RunTranscriptionJobInput { jobId: string; trackId: string; provider: DJSEMTranscriptionProvider; language?: string; hasAudio?: boolean; createdAt?: string; }
/** Input for {@link TranscriptionPipelineService.importManualTranscript}. */
export interface ImportManualTranscriptInput {
  trackId: string; text?: string; segments?: Array<Partial<DJSEMTranscriptSegment>>; language?: string;
  rightsStatus?: DJSEMTranscriptArtifact["rightsStatus"]; createdAt?: string;
}
/** Input for {@link TranscriptionPipelineService.createPhraseAlignment}. */
export interface CreatePhraseAlignmentInput { artifact: DJSEMTranscriptArtifact; chapterBoundariesMs?: Array<{ chapterId: string; startTimeMs: number; endTimeMs: number }>; }

const clampUnit = (n: number | undefined, d = 0.5) => Math.max(0, Math.min(1, typeof n === "number" && Number.isFinite(n) ? n : d));

// ---- service (class-based; hard rule 12) ------------------------------------

const _seq = new SeqCounter();
/**
 * Pure, provider-agnostic transcription pipeline. The live runtime (Cloudflare Workers AI
 * Whisper binding, R2 audio, D1 jobs) lives in apps/transcription-worker and is not duplicated
 * here. Manual import always works with no provider; instrumental tracks are valid (skipped,
 * not failed); a failing provider yields a failed job, never a crash.
 */
export class TranscriptionPipelineService {
  /**
   * Request transcription for a track, skipping instrumental tracks and suppressing duplicate jobs by idempotency key.
   * @param input - the track id, optional provider/language, instrumental flag, and idempotency key.
   * @returns the job result (queued, or skipped_instrumental).
   */
  async requestTranscription(input: RequestTranscriptionInput): Promise<TranscriptionJobResult> {
    const provider = input.provider ?? "cloudflare_workers_ai_whisper";
    // Instrumental / no-lyrics tracks are valid — skip, don't fail.
    if (input.isInstrumental) {
      return { jobId: this.jobId(input), trackId: input.trackId, status: "skipped_instrumental", provider, warnings: ["Track marked instrumental; transcription skipped."] };
    }
    // Idempotency: a job with the same key already queued is not duplicated.
    const key = input.idempotencyKey ?? `${input.trackId}:${provider}`;
    if ((input.existingJobKeys ?? []).includes(key)) {
      return { jobId: `existing_${key}`, trackId: input.trackId, status: "queued", provider, warnings: ["Duplicate suppressed: a job with this key is already queued."] };
    }
    return { jobId: this.jobId(input), trackId: input.trackId, status: "queued", provider, warnings: [] };
  }

  /**
   * Run a queued transcription job against a provider, never throwing — failures produce a "failed" job result.
   * @param input - the job id, track id, provider instance, and optional language/audio flags.
   * @returns the job result (completed with an artifact, or failed with an error message).
   */
  async runQueuedJob(input: RunTranscriptionJobInput): Promise<TranscriptionJobResult> {
    try {
      const canRun = await input.provider.canTranscribe({ trackId: input.trackId, hasAudio: !!input.hasAudio, language: input.language });
      if (!canRun) {
        return { jobId: input.jobId, trackId: input.trackId, status: "failed", provider: input.provider.providerId, warnings: [], error: "Provider cannot run for this track (no audio or unsupported)." };
      }
      const artifact = await input.provider.transcribe({ trackId: input.trackId, language: input.language, audioAvailable: !!input.hasAudio, createdAt: input.createdAt });
      return { jobId: input.jobId, trackId: input.trackId, status: "completed", provider: input.provider.providerId, artifact, warnings: artifact.audit.warnings };
    } catch (e) {
      // A failing provider produces a FAILED job — never crashes the library.
      return { jobId: input.jobId, trackId: input.trackId, status: "failed", provider: input.provider.providerId, warnings: [], error: e instanceof Error ? e.message : String(e) };
    }
  }

  /**
   * Import a manually-entered transcript (full text and/or segments).
   * @param input - the track id, text/segments, optional language, and rights status.
   * @returns the finalized transcript artifact.
   */
  async importManualTranscript(input: ImportManualTranscriptInput): Promise<DJSEMTranscriptArtifact> {
    const createdAt = input.createdAt ?? "1970-01-01T00:00:00.000Z";
    const segments: DJSEMTranscriptSegment[] = (input.segments ?? []).map((s, i) => ({
      id: s.id ?? `seg_${i}`, startTimeMs: s.startTimeMs, endTimeMs: s.endTimeMs,
      text: s.text ?? "", confidence: clampUnit(s.confidence, 1), speaker: s.speaker, warnings: s.warnings ?? [],
    }));
    const fullText = input.text ?? (segments.map((s) => s.text).join(" ").trim() || undefined);
    const rightsStatus = input.rightsStatus ?? "unknown";
    return this.finalizeArtifact({
      trackId: input.trackId, sourceType: "manual_import", provider: "manual_import",
      language: input.language, segments, fullText, rightsStatus, createdAt, fallbackUsed: false,
      confidence: segments.length ? clampUnit(avg(segments.map((s) => s.confidence)), 1) : 1,
    });
  }

  /**
   * Validate a transcript artifact's required fields, confidence bounds, and rights/export-policy consistency.
   * @param input - the (untyped) value to validate.
   * @returns validation result.
   */
  validateTranscript(input: unknown): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    const a = input as DJSEMTranscriptArtifact;
    if (!a || typeof a !== "object") return toDJSEMValidationResult({ valid: false, issues: [{ path: "$", message: "not an object" }] }, "transcript");
    if (!a.trackId) issues.push({ path: "$.trackId", message: "trackId required" });
    if (!(a.confidence >= 0 && a.confidence <= 1)) issues.push({ path: "$.confidence", message: "confidence must be 0..1" });
    for (let i = 0; i < (a.segments ?? []).length; i++) {
      const s = a.segments[i];
      if (!(s.confidence >= 0 && s.confidence <= 1)) issues.push({ path: `$.segments[${i}].confidence`, message: "segment confidence must be 0..1" });
      if (typeof s.startTimeMs === "number" && typeof s.endTimeMs === "number" && s.startTimeMs > s.endTimeMs) issues.push({ path: `$.segments[${i}]`, message: "startTimeMs must be <= endTimeMs" });
    }
    // Rights: restricted transcripts must not be publicly exportable.
    if (a.rightsStatus === "restricted" && a.usagePolicy?.canDisplayInPublicExport) issues.push({ path: "$.usagePolicy.canDisplayInPublicExport", message: "restricted rights cannot allow public export" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "transcript");
  }

  /**
   * Align a transcript's segments into phrases, suggesting a chapter id when segment timing falls within a chapter boundary.
   * @param input - the transcript artifact and optional chapter boundaries.
   * @returns the phrase alignment.
   */
  createPhraseAlignment(input: CreatePhraseAlignmentInput): DJSEMTranscriptPhraseAlignment {
    const bounds = input.chapterBoundariesMs ?? [];
    const phrases = input.artifact.segments.map((s, i) => {
      const mid = typeof s.startTimeMs === "number" && typeof s.endTimeMs === "number" ? (s.startTimeMs + s.endTimeMs) / 2 : s.startTimeMs;
      const chapter = typeof mid === "number" ? bounds.find((b) => mid >= b.startTimeMs && mid < b.endTimeMs) : undefined;
      const warnings: string[] = [];
      if (typeof s.startTimeMs !== "number") warnings.push("Segment has no timing; alignment is text-only.");
      return { id: `ph_${s.id}`, startTimeMs: s.startTimeMs, endTimeMs: s.endTimeMs, text: s.text, confidence: clampUnit(s.confidence, 0.5), suggestedChapterId: chapter?.chapterId, warnings };
    });
    return { alignmentVersion: "1.0", phrases };
  }

  /**
   * Compute a deterministic content hash for a transcript artifact (excludes createdAt so identical content hashes equal).
   * @param artifact - the artifact to hash.
   * @returns the deterministic hash.
   */
  artifactHash(artifact: DJSEMTranscriptArtifact): string {
    return deterministicHash({ trackId: artifact.trackId, sourceType: artifact.sourceType, language: artifact.language, segments: artifact.segments, fullText: artifact.fullText, rightsStatus: artifact.rightsStatus });
  }

  /**
   * Shared finalizer used by manual import and providers: computes rights-derived usage policy and the artifact id.
   * @param x - the raw artifact fields (source, segments, rights status, etc).
   * @returns the finalized transcript artifact.
   */
  finalizeArtifact(x: {
    trackId: string; sourceType: DJSEMTranscriptSourceType; provider: DJSEMTranscriptSourceType;
    language?: string; segments: DJSEMTranscriptSegment[]; fullText?: string;
    rightsStatus: DJSEMTranscriptArtifact["rightsStatus"]; createdAt: string; fallbackUsed: boolean; confidence: number;
  }): DJSEMTranscriptArtifact {
    const warnings: string[] = [];
    if (x.rightsStatus === "unknown") warnings.push("Transcript rights unknown; not cleared for public export.");
    const canPublic = x.rightsStatus === "owned" || x.rightsStatus === "licensed";
    const artifact: DJSEMTranscriptArtifact = {
      transcriptId: "", trackId: x.trackId, transcriptVersion: "0.8B", sourceType: x.sourceType,
      language: x.language, segments: x.segments, fullText: x.fullText, confidence: clampUnit(x.confidence, 1),
      rightsStatus: x.rightsStatus,
      usagePolicy: {
        canDisplayInPrep: x.rightsStatus !== "restricted",
        canDisplayInPublicExport: canPublic,
        canUseForSemanticAnalysis: x.rightsStatus !== "restricted",
        warnings,
      },
      audit: { createdAt: x.createdAt, provider: x.provider, warnings, fallbackUsed: x.fallbackUsed },
    };
    artifact.transcriptId = `tr_${x.trackId}_${this.artifactHash(artifact)}`;
    return artifact;
  }

  private jobId(input: { trackId: string; createdAt?: string }): string { return `job_${input.trackId}_${_seq.next()}`; }
}

function avg(xs: number[]): number { return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0; }

/**
 * A trivial manual provider — always available, no external binding. Proves the pipeline works
 * even with no Whisper binding, by replaying a preset manual transcript.
 */
export class ManualTranscriptionProvider implements DJSEMTranscriptionProvider {
  providerId: DJSEMTranscriptSourceType = "manual_import";
  private svc = new TranscriptionPipelineService();
  private preset: ImportManualTranscriptInput;
  constructor(preset: ImportManualTranscriptInput) { this.preset = preset; }
  async canTranscribe(): Promise<boolean> { return true; }
  async transcribe(input: TranscriptionProviderInput): Promise<DJSEMTranscriptArtifact> {
    return this.svc.importManualTranscript({ ...this.preset, trackId: input.trackId, createdAt: input.createdAt });
  }
}
